import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppUpdates } from "@/lib/appUpdatesStore";
import { Sparkles, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, LogOut, User, FileText, Menu, X, 
  MessageSquare, Calendar, BookOpen, ChevronDown, ChevronRight,
  Users, Plus, Receipt, BarChart3, ClipboardList, AlertCircle,
  HelpCircle, Tag, MessagesSquare, Bell, Syringe, MapPin, Megaphone, Ban, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { pollGet, runPollTasks, startPolling } from "@/utils/layoutPolling";
import MarketingNotificationsBell from "@/components/marketing/MarketingNotificationsBell";
import MobileStickyHeader from "@/components/layout/MobileStickyHeader";
import PullToRefresh from "@/components/layout/PullToRefresh";
import PendingNakesRequestBanner from "@/components/PendingNakesRequestBanner";
import PendingOTWCancelBanner from "@/components/PendingOTWCancelBanner";
import MarketingDailyReportPopup from "@/components/marketing/MarketingDailyReportPopup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collapsible Menu Group Component
const MenuGroup = ({ icon: Icon, label, children, badge, isExpanded, onToggle, isActive }) => {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "text-gray-700 hover:bg-gray-100"
        }`}
      >
        <div className="flex items-center space-x-3">
          <Icon className="w-5 h-5" />
          <span className="font-medium">{label}</span>
        </div>
        <div className="flex items-center space-x-2">
          {badge > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {badge}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ to, icon: Icon, label, badge, onClick, isActive, highlight }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-100 text-blue-700 font-medium"
          : highlight
          ? "bg-amber-50 text-amber-700 border border-amber-200"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        {Icon && <Icon className={`w-4 h-4 ${highlight ? 'text-amber-600' : ''}`} />}
        <span className="text-sm">{label}</span>
      </div>
      {badge > 0 && (
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full min-w-[18px] text-center ${
          highlight ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {badge}
        </span>
      )}
    </Link>
  );
};

// Standalone Menu Item Component
const StandaloneMenuItem = ({ to, icon: Icon, label, badge, onClick, isActive, highlight }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-50 text-blue-700 font-medium"
          : highlight
          ? "bg-amber-50 text-amber-700 border border-amber-200"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        <Icon className={`w-5 h-5 ${highlight ? 'text-amber-600' : ''}`} />
        <span className="font-medium">{label}</span>
      </div>
      {badge > 0 && (
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center ${
          highlight ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {badge}
        </span>
      )}
    </Link>
  );
};

export default function MarketingLayout() {
  const { user, logout } = useAuth();
  const { count: appUpdatesCount } = useAppUpdates();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Height (px) of PendingNakesRequestBanner when visible on mobile.
  const [bannerHeight, setBannerHeight] = useState(0);

  // iter388: Track when AllChats has a thread open (URL doesn't change there).
  const [isAllChatsThreadOpen, setIsAllChatsThreadOpen] = useState(false);
  useEffect(() => {
    const onEvent = (e) => setIsAllChatsThreadOpen(!!(e.detail && e.detail.active));
    window.addEventListener('vaxa:allchats-thread-active', onEvent);
    if (!location.pathname.includes('/all-chats')) {
      setIsAllChatsThreadOpen(false);
    }
    return () => window.removeEventListener('vaxa:allchats-thread-active', onEvent);
  }, [location.pathname]);
  
  // Badge counts
  const [unreadDiscussionsCount, setUnreadDiscussionsCount] = useState(0);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  const [unreadOrdersCount, setUnreadOrdersCount] = useState(0);
  // iter385: unified chat badge summary — same endpoint as AdminLayout.
  const [badgeSummary, setBadgeSummary] = useState({
    chat: { all_chat: 0, chat_admin: 0, chat_nakes: 0, job_discussions: 0, chat_gudang: 0, coordination_channels: 0, total: 0 },
    informasi: { total: 0 },
    orders: { total: 0 },
    system: { total: 0 },
    grand_total: 0,
  });
  
  // Tasks count for Marketing
  const [tasksCount, setTasksCount] = useState(0);
  const [invoicePendingCount, setInvoicePendingCount] = useState(0);
  
  // Marketing Operational Mode State
  const [marketingModeStatus, setMarketingModeStatus] = useState(null);

  // Collapsible state - load from localStorage
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('marketing_sidebar_expanded_v2');
      return saved ? JSON.parse(saved) : { orderManagement: true, chat: false, customer: false };
    } catch {
      return { orderManagement: true, chat: false, customer: false };
    }
  });

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('marketing_sidebar_expanded_v2', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand group based on current route
  useEffect(() => {
    const path = location.pathname;
    
    // Order Management routes
    const orderRoutes = ['/marketing/orders', '/marketing/schedule', '/marketing/invoices'];
    const isOrderRoute = orderRoutes.some(route => path.startsWith(route));
    
    // Chat routes
    const chatRoutes = ['/marketing/discussions', '/marketing/coordination-channels', '/marketing/chats/finance', '/marketing/all-chats', '/marketing/job-discussions'];
    const isChatRoute = chatRoutes.some(route => path.startsWith(route));
    
    if (isOrderRoute && !expandedGroups.orderManagement) {
      setExpandedGroups(prev => ({ ...prev, orderManagement: true }));
    }
    const customerRoutes = ['/marketing/customers', '/marketing/vaccine-data-list', '/marketing/promo-info', '/marketing/daftar-block', '/marketing/vouchers'];
    const isCustomerRoute = customerRoutes.some(route => path.startsWith(route));
    if (isCustomerRoute && !expandedGroups.customer) {
      setExpandedGroups(prev => ({ ...prev, customer: true }));
    }
    if (isChatRoute && !expandedGroups.chat) {
      setExpandedGroups(prev => ({ ...prev, chat: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Handler for clicking Orders menu - immediately clear badge
  const handleOrdersClick = async () => {
    closeSidebar();
    setUnreadOrdersCount(0);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/notifications/orders/mark-seen`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (e) {
      // Silently fail
    }
  };

  // Fetch unread counts and tasks
  // Okt 2026 (Fase 3) — semua hitungan badge + status Mode Malam diambil
  // bersamaan dalam satu putaran dengan timeout (sebelumnya berurutan, tanpa
  // timeout, tiap 10 detik). Mengembalikan false jika semuanya gagal.
  const fetchCounts = async () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    const headers = { Authorization: `Bearer ${token}` };

    return runPollTasks([
      // Fetch notifications summary
      async () => {
        const notifRes = await pollGet(`${API}/notifications/summary`, { headers });
        setUnreadOrdersCount(notifRes.data.new_orders_count || 0);
      },
      // Fetch chat discussions (Marketing ↔ Admin) — kept for
      // `unreadDiscussionsCount` (still used elsewhere in this layout).
      async () => {
        const response = await pollGet(`${API}/discussions`, { headers });
        const unreadCount = response.data.filter(d => d.my_unread_count > 0).length;
        setUnreadDiscussionsCount(unreadCount);
      },
      // iter385: Unified badge summary — same endpoint as AdminLayout. Feeds
      // Chat parent + all Chat sub-items (Semua Chat, Chat Admin, Group Diskusi,
      // Kanal Koordinasi) so marketing sees parity with admin.
      async () => {
        const badgeRes = await pollGet(`${API}/notifications/badge-summary`, { headers });
        setBadgeSummary(badgeRes.data);
        // iter391 — PWA app icon badge on installed devices.
        try {
          if ('setAppBadge' in navigator) {
            const gt = badgeRes.data?.grand_total || 0;
            if (gt > 0) navigator.setAppBadge(gt);
            else navigator.clearAppBadge?.();
          }
        } catch (_e) { /* silent */ }
      },
      // Fetch unread announcements
      async () => {
        const announcementsRes = await pollGet(`${API}/knowledge-center/announcements/unread-count`, { headers });
        setUnreadAnnouncementsCount(announcementsRes.data.unread_count || 0);
      },
      // Fetch marketing tasks count
      async () => {
        const tasksRes = await pollGet(`${API}/marketing/tasks/count`, { headers });
        setTasksCount(tasksRes.data.total || 0);
        setInvoicePendingCount(tasksRes.data.invoice_pending || 0);
      },
      // Fetch Marketing Mode Status
      async () => {
        const response = await pollGet(`${API}/marketing-mode/status`, { headers });
        setMarketingModeStatus(response.data);
      },
    ]);
  };

  useEffect(() => {
    // Okt 2026 (Fase 3) — polling 30 detik lewat startPolling: tidak menumpuk,
    // berhenti saat tab tersembunyi atau offline, backoff saat gagal.
    const stopPolling = startPolling(fetchCounts);
    return () => {
      stopPolling();
    };
  }, []);

  // Check if route is active
  const isRouteActive = (path) => {
    if (path === '/marketing/orders') {
      return location.pathname === path || location.pathname.startsWith('/marketing/orders/');
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Check if any route in group is active
  const isOrderManagementActive = ['/marketing/orders', '/marketing/schedule', '/marketing/invoices', '/marketing/direktori-nakes']
    .some(route => location.pathname.startsWith(route));

  const isCustomerActive = ['/marketing/customers', '/marketing/vaccine-data-list', '/marketing/promo-info', '/marketing/daftar-block', '/marketing/vouchers']
    .some(route => location.pathname.startsWith(route));
  
  const isChatActive = ['/marketing/discussions', '/marketing/coordination-channels', '/marketing/all-chats', '/marketing/job-discussions', '/marketing/chats/finance']
    .some(route => location.pathname.startsWith(route));

  // Total badges
  const totalOrderBadge = unreadOrdersCount;
  // iter385: unified badge → derives from /notifications/badge-summary
  const totalChatBadge = badgeSummary.chat?.total || 0;

  return (
    <div className="min-h-screen bg-gray-50 lg:overflow-hidden lg:h-screen">
      {/* Overlay for mobile/tablet */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - FIXED on desktop (>=1024px) */}
      <div className={`
        fixed inset-y-0 left-0 z-40
        w-64 bg-white border-r border-gray-200 flex flex-col h-screen
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Menu Items - Scrollable (sidebar header removed — branding now in MobileStickyHeader) */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto pt-14 lg:pt-4">
          {/* Marketing Operational Mode Status Badge — kept as functional indicator */}
          {marketingModeStatus && marketingModeStatus.can_nakes_ops && (
            <div className="mb-3 p-2 rounded-lg bg-green-50 border border-green-200" data-testid="marketing-mode-badge">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs font-medium text-green-700">
                  Mode Operasional Aktif
                </span>
              </div>
              <p className="text-[10px] text-green-600 mt-1">
                Dapat assign Nakes & share job
              </p>
            </div>
          )}
          {/* Dashboard - Control Tower */}
          <StandaloneMenuItem
            to="/marketing/dashboard"
            icon={LayoutDashboard}
            label="Dashboard"
            badge={tasksCount}
            onClick={closeSidebar}
            isActive={location.pathname === '/marketing/dashboard'}
            highlight={tasksCount > 0}
            data-testid="menu-dashboard"
          />

          {/* ORDER MANAGEMENT - Collapsible Group */}
          <MenuGroup
            icon={FileText}
            label="Kelola Order"
            badge={totalOrderBadge}
            isExpanded={expandedGroups.orderManagement}
            onToggle={() => toggleGroup('orderManagement')}
            isActive={isOrderManagementActive}
          >
            <MenuItem
              to="/marketing/orders/new"
              icon={Plus}
              label="Order Baru"
              onClick={closeSidebar}
              isActive={isRouteActive('/marketing/orders/new')}
            />
            <MenuItem
              to="/marketing/orders"
              icon={FileText}
              label="Daftar Order"
              badge={unreadOrdersCount}
              onClick={handleOrdersClick}
              isActive={location.pathname === '/marketing/orders' || (location.pathname.startsWith('/marketing/orders/') && !location.pathname.includes('/new'))}
            />
            <MenuItem
              to="/marketing/schedule"
              icon={Calendar}
              label="Jadwal"
              onClick={closeSidebar}
              isActive={isRouteActive('/marketing/schedule')}
            />
            <MenuItem
              to="/marketing/direktori-nakes"
              icon={MapPin}
              label="Direktori Nakes"
              onClick={closeSidebar}
              isActive={isRouteActive('/marketing/direktori-nakes')}
            />
            <MenuItem
              to="/marketing/invoices"
              icon={Receipt}
              label="Monitor Invoice"
              badge={invoicePendingCount}
              onClick={closeSidebar}
              isActive={isRouteActive('/marketing/invoices')}
              highlight={invoicePendingCount > 0}
            />
          </MenuGroup>

          {/* MANAJEMEN CUSTOMER - Collapsible Group (Okt 2026) */}
          <MenuGroup
            icon={Users}
            label="Manajemen Customer"
            isExpanded={expandedGroups.customer}
            onToggle={() => toggleGroup('customer')}
            isActive={isCustomerActive}
          >
            <MenuItem
              to="/marketing/customers/master"
              icon={Users}
              label="Data Customer"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/marketing/customers')}
            />
            <MenuItem
              to="/marketing/vaccine-data-list"
              icon={Syringe}
              label="List Data Vaksin"
              onClick={closeSidebar}
              isActive={location.pathname === '/marketing/vaccine-data-list'}
            />
            <MenuItem
              to="/marketing/promo-info"
              icon={Tag}
              label="Info Promo"
              onClick={closeSidebar}
              isActive={location.pathname === '/marketing/promo-info'}
            />
            <MenuItem
              to="/marketing/daftar-block"
              icon={Ban}
              label="Daftar Block"
              onClick={closeSidebar}
              isActive={location.pathname === '/marketing/daftar-block'}
            />
            <MenuItem
              to="/marketing/vouchers"
              icon={Tag}
              label="Voucher"
              onClick={closeSidebar}
              isActive={isRouteActive('/marketing/vouchers')}
            />
          </MenuGroup>

          {/* CHAT — Fix 90 (Feb 2026): sidebar submenu dihapus. Klik "Chat"
              langsung navigate ke /marketing/all-chats. Filter per tipe
              (All Chat / Admin / Diskusi / Gudang / Nakes / Marketing) muncul
              sebagai chip di dalam halaman AllChats.
          */}
          <StandaloneMenuItem
            to="/marketing/all-chats"
            icon={MessageSquare}
            label="Chat"
            badge={totalChatBadge}
            onClick={closeSidebar}
            isActive={isChatActive}
          />

          {/* Pusat Informasi - Marketing-specific route */}
          <StandaloneMenuItem
            to="/marketing/knowledge-center"
            icon={BookOpen}
            label="Pusat Informasi"
            badge={unreadAnnouncementsCount}
            onClick={closeSidebar}
            isActive={location.pathname.startsWith('/marketing/knowledge-center') || location.pathname === '/marketing/tutorial'}
          />

          {/* Nov 2026 — Cara Pakai Aplikasi (panduan marketing: Alur Kerja, Menu, Situasi) */}
          <StandaloneMenuItem
            to="/marketing/bantuan"
            icon={LifeBuoy}
            label="Cara Pakai Aplikasi"
            onClick={closeSidebar}
            isActive={location.pathname === '/marketing/bantuan'}
          />

          {/* Okt 2026 — Apa yang Baru */}
          <StandaloneMenuItem
            to="/marketing/apa-yang-baru"
            icon={Sparkles}
            label="Apa yang Baru"
            badge={appUpdatesCount}
            onClick={closeSidebar}
            isActive={location.pathname === '/marketing/apa-yang-baru'}
          />

          {/* Reports */}
          <StandaloneMenuItem
            to="/marketing/reports"
            icon={BarChart3}
            label="Laporan"
            onClick={closeSidebar}
            isActive={location.pathname === '/marketing/reports'}
          />
          
          {/* Daily Report - KPI Input */}
          <StandaloneMenuItem
            to="/marketing/reports/daily"
            icon={ClipboardList}
            label="Daily Report (KPI)"
            onClick={closeSidebar}
            isActive={location.pathname === '/marketing/reports/daily'}
          />
          
          {/* Complaint Nakes (Marketing dapat lapor + lihat SP read-only) */}
          <StandaloneMenuItem
            to="/marketing/nakes-performance/complaints"
            icon={AlertCircle}
            label="Komplain Nakes"
            onClick={closeSidebar}
            isActive={location.pathname.startsWith('/marketing/nakes-performance/complaints')}
          />

          <Link
            to="/marketing/papan-operasional"
            onClick={closeSidebar}
            className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
              location.pathname.startsWith('/marketing/papan-operasional')
                ? 'bg-blue-50 text-blue-700 font-medium'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Megaphone className="w-5 h-5" />
            <span>Papan Operasional</span>
          </Link>

          {/* Okt 2026 — Akun yang role utamanya Finance dan merangkap Marketing: tombol kembali */}
          {user?.primary_role === "finance" && (
            <Link
              to="/finance/dashboard"
              onClick={closeSidebar}
              className="flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors bg-amber-50 text-amber-700 border border-amber-200 mt-2"
              data-testid="back-to-finance"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Kembali ke Finance</span>
            </Link>
          )}
        </nav>

        {/* Bottom: Profile row (with bell right) + Logout row */}
        <div className="flex-shrink-0 p-2 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          <div className="flex items-center gap-2 mb-2">
            <Link
              to="/marketing/profile"
              onClick={closeSidebar}
              className="flex items-center gap-2 flex-1 min-w-0 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="profile-link"
            >
              <User className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">{user?.name}</p>
                <p className="text-[10px] text-gray-500 truncate leading-tight">{user?.user_id} · Marketing</p>
              </div>
            </Link>

            <div className="flex-shrink-0" data-testid="sidebar-notification-bell">
              <MarketingNotificationsBell />
            </div>
          </div>

          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content - Scrollable area, offset by sidebar width on desktop */}
      <div className="flex-1 min-w-0 min-h-screen lg:ml-64 lg:h-screen lg:overflow-y-auto">
        {/* iter386: hide when inside a chat room so the room's own
            <ChatHeaderMobile> isn't covered.
            iter388: also hide when a thread is open inside /marketing/all-chats
            (URL doesn't change there). */}
        {(() => {
          const isInChatRoom = /^\/marketing\/(discussions|job-discussions|coordination-channels|nakes-chats)\/[^/]+/i.test(location.pathname);
          const hideNavbar = isInChatRoom || isAllChatsThreadOpen;
          return (
            <>
              {!hideNavbar && (
                <MobileStickyHeader
                  userName={user?.name || 'User'}
                  userRole="Marketing"
                  sidebarOpen={sidebarOpen}
                  onMenuClick={() => setSidebarOpen(!sidebarOpen)}
                  offsetTop={bannerHeight}
                />
              )}
              <PendingNakesRequestBanner
                onHeightChange={setBannerHeight}
                fixedMobile={!hideNavbar}
              />
              <PendingOTWCancelBanner
                onHeightChange={setBannerHeight}
                fixedMobile={!hideNavbar}
              />
              <div
                className={`w-full h-full ${hideNavbar ? '' : 'pt-[calc(3.5rem+var(--banner-h,0px))] lg:pt-0 pb-20 lg:pb-0'}`}
                style={hideNavbar ? undefined : { '--banner-h': `${bannerHeight}px` }}
              >
                <PullToRefresh>
                  <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
                    <Outlet />
                  </div>
                </PullToRefresh>
              </div>
            </>
          );
        })()}
      </div>
      
      {/* Okt 2026 — Pop up wajib Report Harian (report kemarin). Tidak muncul
          saat marketing sedang membuka ruang chat. */}
      <MarketingDailyReportPopup
        suppress={
          isAllChatsThreadOpen ||
          /^\/marketing\/(discussions|job-discussions|coordination-channels|nakes-chats|chats\/finance)\/[^/]+/i.test(location.pathname)
        }
      />
    </div>
  );
}
